import React, { useState, useEffect, useContext, useMemo, useRef } from "react";
import { useUser } from "../context/UserContext";
import SignInPage from "../pages/signinpage";
import search1 from "../images/search-1.svg";
import { useTable, useSortBy, usePagination } from "react-table";
import "../searchstyles.css";
import axios from "../api/client";
import { getFavorites } from "../api/private";
import debounce from "lodash.debounce";
import { CSVLink } from "react-csv";
import lock from "../images/PremiumLock.png";
import "../scanner-mobile-fix.css";

// ---- FILTER CONFIG (keeps UI styles; just renders more rows) ----

function useUsFederalHolidays(year) {
  const nthWeekdayOfMonth = (y, m, weekday, n) => {
    // weekday: 0=Sun...6=Sat; m: 0=Jan...11=Dec
    const first = new Date(Date.UTC(y, m, 1));
    const firstW = first.getUTCDay();
    const delta = (weekday - firstW + 7) % 7;
    return new Date(Date.UTC(y, m, 1 + delta + 7 * (n - 1)));
  };
  const lastWeekdayOfMonth = (y, m, weekday) => {
    // last occurrence of weekday in month
    const last = new Date(Date.UTC(y, m + 1, 0));
    const lastW = last.getUTCDay();
    const delta = (lastW - weekday + 7) % 7;
    return new Date(Date.UTC(y, m + 1, 0 - delta));
  };
  const observed = (dt) => {
    // Observed rule: if Sat -> Friday; if Sun -> Monday
    const w = dt.getUTCDay();
    if (w === 6) return new Date(Date.UTC(dt.getUTCFullYear(), dt.getUTCMonth(), dt.getUTCDate() - 1));
    if (w === 0) return new Date(Date.UTC(dt.getUTCFullYear(), dt.getUTCMonth(), dt.getUTCDate() + 1));
    return dt;
  };
  const iso = (dt) => dt.toISOString().slice(0, 10);

  // Build all federal holidays (actual + observed)
  const y = year;
  const holidays = [
    { name: "New Year's Day", date: new Date(Date.UTC(y, 0, 1)) },
    { name: "Martin Luther King Jr. Day", date: nthWeekdayOfMonth(y, 0, 1, 3) }, // 3rd Mon Jan
    { name: "Washington's Birthday", date: nthWeekdayOfMonth(y, 1, 1, 3) }, // 3rd Mon Feb
    { name: "Memorial Day", date: lastWeekdayOfMonth(y, 4, 1) }, // last Mon May
    { name: "Juneteenth National Independence Day", date: new Date(Date.UTC(y, 5, 19)) },
    { name: "Independence Day", date: new Date(Date.UTC(y, 6, 4)) },
    { name: "Labor Day", date: nthWeekdayOfMonth(y, 8, 1, 1) }, // 1st Mon Sep
    { name: "Columbus Day", date: nthWeekdayOfMonth(y, 9, 1, 2) }, // 2nd Mon Oct
    { name: "Veterans Day", date: new Date(Date.UTC(y, 10, 11)) },
    { name: "Thanksgiving Day", date: nthWeekdayOfMonth(y, 11, 4, 4) }, // 4th Thu Nov
    { name: "Christmas Day", date: new Date(Date.UTC(y, 11, 25)) },
  ].map((h) => ({
    name: h.name,
    actual: iso(h.date),
    observed: iso(observed(h.date)),
  }));

  return holidays;
}

function getEasternNowParts() {
  // Use Intl to get America/New_York parts (no external libs)
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    hour12: false,
    weekday: "short",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).formatToParts(new Date());

  const grab = (type) => parts.find((p) => p.type === type)?.value;
  return {
    weekdayShort: grab("weekday"), // "Mon"... "Sun"
    year: Number(grab("year")),
    month: Number(grab("month")), // "01".."12"
    day: Number(grab("day")),
    hour: Number(grab("hour")), // 00..23 ET
    minute: Number(grab("minute")),
  };
}

function isUsTradingOpenET({ weekdayShort, hour, minute }) {
  // Mon–Fri
  const isWeekday = ["Mon", "Tue", "Wed", "Thu", "Fri"].includes(weekdayShort);
  if (!isWeekday) return false;

  // Regular hours 9:30–16:00 ET
  const afterOpen = hour > 9 || (hour === 9 && minute >= 30);
  const beforeClose = hour < 16;
  return afterOpen && beforeClose;
}












const FILTER_SECTIONS = {
  valuation: [
    { key: "price", label: "Price" },
    { key: "peTTM", label: "P/E" },
    { key: "pbQuarterly", label: "P/B" },
    { key: "psTTM", label: "P/S" },
    { key: "enterpriseValue", label: "Enterprise Value" },
    { key: "evToEbitdaTTM", label: "EV/EBITDA" },
    { key: "evToSalesTTM", label: "EV/Sales" },
    { key: "priceToBookTangible", label: "P/Tangible Book" },
  ],
  liquidity: [
    { key: "currentRatioQuarterly", label: "Current Ratio" },
    { key: "quickRatioQuarterly", label: "Quick Ratio" },
    { key: "cashRatioQuarterly", label: "Cash Ratio" },
  ],
  growth: [
    { key: "revenueGrowthYoYTTM", label: "Revenue YoY (TTM)", unit: "%", inputScale: 100 },
    { key: "epsGrowthYoYTTM", label: "EPS YoY (TTM)", unit: "%", inputScale: 100 },
    { key: "revenueCAGR3Y", label: "Revenue CAGR (3Y)", unit: "%", inputScale: 100 },
    { key: "epsCAGR3Y", label: "EPS CAGR (3Y)", unit: "%", inputScale: 100 },
    { key: "revenueGrowthYoYLastQ", label: "Rev YoY (LastQ)", unit: "%", inputScale: 100 },
    { key: "revenueGrowthQoQLastQ", label: "Rev QoQ (LastQ)", unit: "%", inputScale: 100 },
    { key: "netIncomeGrowthYoYLastQ", label: "NI YoY (LastQ)", unit: "%", inputScale: 100 },
    { key: "netIncomeGrowthQoQLastQ", label: "NI QoQ (LastQ)", unit: "%", inputScale: 100 },
  ],
    quality: [
    { key: "altmanZ", label: "Altman Z" },
    { key: "piotroskiF", label: "Piotroski F" },
  ],
  profitability: [
    { key: "grossMarginTTM", label: "Gross Margin", unit: "%", inputScale: 100 },
    { key: "operatingMarginTTM", label: "Operating Margin", unit: "%", inputScale: 100 },
    { key: "profitMarginTTM", label: "Net Margin", unit: "%", inputScale: 100 },
    { key: "fcfMarginTTM", label: "FCF Margin", unit: "%", inputScale: 100 },
    { key: "roaTTM", label: "ROA", unit: "%", inputScale: 100 },
    { key: "roeTTM", label: "ROE", unit: "%", inputScale: 100 },
    { key: "roicTTM", label: "ROIC", unit: "%", inputScale: 100 },
  ],
  leverage: [
    { key: "debtToAssets", label: "Debt/Assets" },
    { key: "debtToEquity", label: "Debt/Equity" },
    { key: "netDebtToEBITDA", label: "Net Debt/EBITDA" },
    { key: "interestCoverageTTM", label: "Interest Coverage" },
  ],

   dcf: [
    { key: "DCFmean", label: "DCF Mean ($)" },
    { key: "DCFmedian", label: "DCF Median ($)" },
    { key: "DCFvalue", label: "DCF Value ($)" },
    { key: "dcfMedianVsPricePct", label: "DCF vs Price", unit: "%", inputScale: 1 }, // -90 means DCF << price
  ],
};

const FILTER_LABELS = Object.values(FILTER_SECTIONS)
  .flat()
  .reduce((labels, filter) => ({ ...labels, [filter.key]: filter.label }), {});

const FILTER_META = Object.values(FILTER_SECTIONS)
  .flat()
  .reduce((meta, filter) => ({ ...meta, [filter.key]: filter }), {});

const UNIVERSE_LABELS = {
  sp500: "SP500",
  rus2000: "Russell2000",
  nq100: "Nasdaq100",
  all: "AllStocks",
  watchlist: "MyWatchlist",
};

const sanitizeFilenamePart = (value) =>
  String(value)
    .trim()
    .replace(/\s+/g, "-")
    .replace(/[^a-zA-Z0-9._-]/g, "")
    .replace(/-+/g, "-")
    .replace(/^[-_.]+|[-_.]+$/g, "");

const shortenSecurityName = (value) => {
  const original = String(value || "").trim();
  if (!original) return "-";

  return original
    .replace(/\s+(?:American\s+)?Depositary\s+(?:Shares?|Receipts?).*$/i, "")
    .replace(/\s+ADS(?:\s+.*)?$/i, "")
    .replace(/\s*\(each representing.*$/i, "")
    .replace(/\s*-\s*(?:Common Stock|Common Shares|Capital Stock|Ordinary Shares).*$/i, "")
    .replace(/\s+(?:Common Stock|Common Shares|Capital Stock|Ordinary Shares).*$/i, "")
    .replace(/\s*-?\s*Class\s+[A-C](?:\s+Common Stock)?$/i, "")
    .replace(/,?\s+S\.A\.B\.\s+de\s+C\.V\.$/i, "")
    .replace(/\s*-\s*$/g, "")
    .trim() || original;
};


// always return a safe string for numbers
const formatNum = (val, digits = 2) => {
  if (val === null || val === undefined || val === "") return "-";
  const numval = Number(val);
  if (!Number.isFinite(numval)) return "-";
  return numval.toFixed(digits);
};

const formatPct = (val, digits = 2) => {
  if (typeof val !== "number" || !isFinite(val)) return "-";
  return (val * 100).toFixed(digits) + "%";
};

const formatBig = (val) => {
  if (typeof val !== "number" || !isFinite(val)) return "-";
  const v = val * 1_000_000; // since you store mcap in millions
  if (v >= 1e12) return (v / 1e12).toFixed(2) + "T";
  if (v >= 1e9) return (v / 1e9).toFixed(2) + "B";
  if (v >= 1e6) return (v / 1e6).toFixed(2) + "M";
  if (v >= 1e3) return (v / 1e3).toFixed(2) + "K";
  return v.toFixed(2);
};




// helpers (number/null; filtering utils)
const numOrNull = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const number = Number(v);
  return Number.isFinite(number) ? number : null;
};

// Data providers commonly use 0 as a placeholder for unavailable valuation
// multiples. A genuine zero multiple is not meaningful, so keep it out of both
// the table and active numeric ranges.
const valuationOrNull = (v) => {
  const number = numOrNull(v);
  return number === 0 ? null : number;
};

const buildEmptyFilters = () => {
  const keys = Array.from(
    new Set(
      Object.values(FILTER_SECTIONS)
        .flat()
        .map((f) => f.key)
    )
  );
  const obj = {};
  keys.forEach((k) => (obj[k] = { min: "", max: "" })); // empty = no constraint
  return obj;
};

const matchesNumRange = (value, min, max) => {
  const hasMin = min !== "" && Number.isFinite(Number(min));
  const hasMax = max !== "" && Number.isFinite(Number(max));
  const hasActiveBound = hasMin || hasMax;
  if (value === null || value === undefined || value === "") return !hasActiveBound;
  const v = Number(value);
  if (!Number.isFinite(v)) return !hasActiveBound;
  if (hasMin && v < Number(min)) return false;
  if (hasMax && v > Number(max)) return false;
  return true;
};





// one place to define every column we can render
const ALL_COLUMN_DEFS = [
  // ---- Basics ----
  {
    id: "security_name",
    Header: "Company Name",
    accessor: (r) => r.security_name ?? "-",
    align: "left",
    Cell: ({ row }) => (
      <a
        href={`/stock/${row.original.symbol}`}
        style={{ textDecoration: "none", color: "inherit" }}
      >
        <span title={row.original.original_security_name || row.original.security_name}>{row.original.security_name ?? "-"}</span>
      </a>
    ),
    group: "Basics",
  },
  {
    id: "symbol",
    Header: "Ticker",
    accessor: (r) => r.symbol ?? "-",
    align: "left",
    Cell: ({ row }) => (
      <a
        href={`/stock/${row.original.symbol}`}
        style={{ textDecoration: "none", color: "inherit" }}
      >
        {row.original.symbol ?? "-"}
      </a>
    ),
    group: "Basics",
  },
  {
    id: "price",
    Header: "Price",
    accessor: (r) => r.price,
    Cell: ({ value }) => formatNum(value, 2),
    group: "Basics",
  },
  
  {
    id: "mcap",
    Header: "Market Cap",
    accessor: (r) => r.mcap,
    Cell: ({ value }) => formatBig(value),
    group: "Basics",
  },
  {
    id: "beta",
    Header: "Beta",
    accessor: (r) => r.beta,
    Cell: ({ value }) => formatNum(value, 2),
    group: "Basics",
  },

  // ---- Valuation ----
  { id: "peTTM", Header: "P/E", accessor: (r) => r.peTTM, Cell: ({ value }) => formatNum(value, 2), group: "Valuation" },
  { id: "psTTM", Header: "P/S", accessor: (r) => r.psTTM, Cell: ({ value }) => formatNum(value, 2), group: "Valuation" },
  { id: "pbQuarterly", Header: "P/B", accessor: (r) => r.pbQuarterly, Cell: ({ value }) => formatNum(value, 2), group: "Valuation" },
  { id: "currentDividendYieldTTM", Header: "Div Yield", accessor: (r) => r.currentDividendYieldTTM, Cell: ({ value }) => formatNum(value, 2), group: "Valuation" },
  { id: "enterpriseValue", Header: "Enterprise Value", accessor: (r) => r.enterpriseValue, Cell: ({ value }) => formatNum(value, 0), group: "Valuation" },
  { id: "evToEbitdaTTM", Header: "EV/EBITDA TTM", accessor: (r) => r.evToEbitdaTTM, Cell: ({ value }) => formatNum(value, 2), group: "Valuation" },
  { id: "evToSalesTTM", Header: "EV/Sales TTM", accessor: (r) => r.evToSalesTTM, Cell: ({ value }) => formatNum(value, 2), group: "Valuation" },
  { id: "priceToBookTangible", Header: "P/Tangible Book", accessor: (r) => r.priceToBookTangible, Cell: ({ value }) => formatNum(value, 2), group: "Valuation" },

  // ---- Totals ----
  { id: "revenueTTM", Header: "Revenue TTM", accessor: (r) => r.revenueTTM, Cell: ({ value }) => formatNum(value, 0), group: "Totals" },
  { id: "netIncomeTTM", Header: "Net Income TTM", accessor: (r) => r.netIncomeTTM, Cell: ({ value }) => formatNum(value, 0), group: "Totals" },
  { id: "ebitdaTTM", Header: "EBITDA TTM", accessor: (r) => r.ebitdaTTM, Cell: ({ value }) => formatNum(value, 0), group: "Totals" },
  { id: "freeCashFlowTTM", Header: "FCF TTM", accessor: (r) => r.freeCashFlowTTM, Cell: ({ value }) => formatNum(value, 0), group: "Totals" },

  // ---- Margins ----
  { id: "grossMarginTTM", Header: "Gross Margin", accessor: (r) => r.grossMarginTTM, Cell: ({ value }) => formatPct(value), group: "Profitability" },
  { id: "operatingMarginTTM", Header: "Operating Margin", accessor: (r) => r.operatingMarginTTM, Cell: ({ value }) => formatPct(value), group: "Profitability" },
  { id: "profitMarginTTM", Header: "Net Margin", accessor: (r) => r.profitMarginTTM, Cell: ({ value }) => formatPct(value), group: "Profitability" },
  { id: "fcfMarginTTM", Header: "FCF Margin", accessor: (r) => r.fcfMarginTTM, Cell: ({ value }) => formatPct(value), group: "Profitability" },

  // ---- Returns ----
  { id: "roaTTM", Header: "ROA", accessor: (r) => r.roaTTM, Cell: ({ value }) => formatPct(value), group: "Profitability" },
  { id: "roeTTM", Header: "ROE", accessor: (r) => r.roeTTM, Cell: ({ value }) => formatPct(value), group: "Profitability" },
  { id: "roicTTM", Header: "ROIC", accessor: (r) => r.roicTTM, Cell: ({ value }) => formatPct(value), group: "Profitability" },

  // ---- Leverage ----
  { id: "debtToAssets", Header: "Debt/Assets", accessor: (r) => r.debtToAssets, Cell: ({ value }) => formatNum(value, 2), group: "Leverage" },
  { id: "debtToEquity", Header: "Debt/Equity", accessor: (r) => r.debtToEquity, Cell: ({ value }) => formatNum(value, 2), group: "Leverage" },
  { id: "netDebtToEBITDA", Header: "Net Debt/EBITDA", accessor: (r) => r.netDebtToEBITDA, Cell: ({ value }) => formatNum(value, 2), group: "Leverage" },
  { id: "interestCoverageTTM", Header: "Interest Coverage", accessor: (r) => r.interestCoverageTTM, Cell: ({ value }) => formatNum(value, 2), group: "Leverage" },

  // ---- Liquidity ----
  { id: "quickRatioQuarterly", Header: "Quick Ratio (Q)", accessor: (r) => r.quickRatioQuarterly, Cell: ({ value }) => formatNum(value, 2), group: "Liquidity" },
  { id: "cashRatioQuarterly", Header: "Cash Ratio (Q)", accessor: (r) => r.cashRatioQuarterly, Cell: ({ value }) => formatNum(value, 2), group: "Liquidity" },

  // ---- Quality ----
  { id: "altmanZ", Header: "Altman Z", accessor: (r) => r.altmanZ, Cell: ({ value }) => formatNum(value, 2), group: "Quality" },
  { id: "piotroskiF", Header: "Piotroski F", accessor: (r) => r.piotroskiF, Cell: ({ value }) => formatNum(value, 0), group: "Quality" },

  // ---- DCF ----
{ id: "DCFmean", Header: "DCF Mean", accessor: (r) => r.DCFmean, Cell: ({ value }) => formatNum(value, 2), group: "DCF" },
{ id: "DCFmedian", Header: "DCF Median", accessor: (r) => r.DCFmedian, Cell: ({ value }) => formatNum(value, 2), group: "DCF" },
{ id: "DCFvalue", Header: "DCF Value", accessor: (r) => r.DCFvalue, Cell: ({ value }) => formatNum(value, 2), group: "DCF" },
{
  id: "dcfMedianVsPricePct",
  Header: "DCF vs Price",
  accessor: (r) => r.dcfMedianVsPricePct,
  Cell: ({ value }) => (value == null ? "-" : `${formatNum(value, 1)}%`),
  group: "DCF",
},
  // ---- Growth ----
  { id: "revenueGrowthYoYTTM", Header: "Revenue YoY (TTM)", accessor: (r) => r.revenueGrowthYoYTTM, Cell: ({ value }) => formatPct(value), group: "Growth" },
  { id: "netIncomeGrowthYoYTTM", Header: "NI YoY (TTM)", accessor: (r) => r.netIncomeGrowthYoYTTM, Cell: ({ value }) => formatPct(value), group: "Growth" },
  { id: "epsGrowthYoYTTM", Header: "EPS YoY (TTM)", accessor: (r) => r.epsGrowthYoYTTM, Cell: ({ value }) => formatPct(value), group: "Growth" },
  { id: "fcfGrowthYoYTTM", Header: "FCF YoY (TTM)", accessor: (r) => r.fcfGrowthYoYTTM, Cell: ({ value }) => formatPct(value), group: "Growth" },
  { id: "revenueCAGR3Y", Header: "Revenue CAGR (3Y)", accessor: (r) => r.revenueCAGR3Y, Cell: ({ value }) => formatPct(value), group: "Growth" },
  { id: "epsCAGR3Y", Header: "EPS CAGR (3Y)", accessor: (r) => r.epsCAGR3Y, Cell: ({ value }) => formatPct(value), group: "Growth" },
  { id: "revenueGrowthYoYLastQ", Header: "Revenue YoY (LastQ)", accessor: (r) => r.revenueGrowthYoYLastQ, Cell: ({ value }) => formatPct(value), group: "Growth" },
  { id: "revenueGrowthQoQLastQ", Header: "Revenue QoQ (LastQ)", accessor: (r) => r.revenueGrowthQoQLastQ, Cell: ({ value }) => formatPct(value), group: "Growth" },
  { id: "netIncomeGrowthYoYLastQ", Header: "NI YoY (LastQ)", accessor: (r) => r.netIncomeGrowthYoYLastQ, Cell: ({ value }) => formatPct(value), group: "Growth" },
  { id: "netIncomeGrowthQoQLastQ", Header: "NI QoQ (LastQ)", accessor: (r) => r.netIncomeGrowthQoQLastQ, Cell: ({ value }) => formatPct(value), group: "Growth" },
];



// default visible (your current table)
const DEFAULT_VISIBLE = [
  "security_name", "symbol", "price", "mcap", "beta",
  "peTTM", "psTTM", "currentDividendYieldTTM", "pbQuarterly", "epsTTM",
];

const MAX_VISIBLE_COLUMNS = 12;
const COLUMN_GROUPS = ["Basics", "DCF", "Growth", "Totals", "Profitability", "Leverage", "Liquidity", "Quality", "Valuation"];







const Scanner = () => {
  const MOBILE_BATCH_SIZE = 8;
  const scannerPanelRef = useRef(null);
  const scannerTableRef = useRef(null);
  const [desktopPageSize, setDesktopPageSize] = useState(12);
  const measureDesktopPageSize = () => {
    const panel = scannerPanelRef.current;
    const table = scannerTableRef.current;
    if (!panel || !table) return 12;
    const header = table.querySelector("thead");
    const firstRow = table.querySelector("tbody tr");
    const pagination = panel.querySelector(".pageSelector");
    const headerBottom = header?.getBoundingClientRect().bottom || table.getBoundingClientRect().top + 36;
    const rowsBottomLimit = pagination?.getBoundingClientRect().top || panel.getBoundingClientRect().bottom - 55;
    const rowHeight = Math.max(32, firstRow?.getBoundingClientRect().height || 36);
    const availableHeight = Math.max(0, rowsBottomLimit - headerBottom - 10);
    return Math.max(8, Math.min(16, Math.floor(availableHeight / rowHeight)));
  };
  const [mobileVisibleCount, setMobileVisibleCount] = useState(MOBILE_BATCH_SIZE);
  const mobileLoadMoreRef = useRef(null);
  const { user, isLoggedIn } = useUser();

  const [marketOpen, setMarketOpen] = useState(false);
  const [sortState, setSortState] = useState([{ id: "symbol", desc: false }]);
  const [stockData, setStockData] = useState([]);
  const [BackupStockData, setBackupStockData] = useState([]);
  const [filterVisible, setFilterVisible] = useState(false);
  const [settingsVisible, setSettingsVisible] = useState(false);
  const [settingsSearch, setSettingsSearch] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
const [filters, setFilters] = useState(buildEmptyFilters());
  const [activeIndex, setActiveIndex] = useState("sp500");

const INDEX_PARAM = {
  sp500: "sp500",
  rus2000: "rus2000",
  nq100: "nq100",
  all: "all",
  watchlist: "all",
};

const scopeKey = (idx) => `scanner.visible.v1.${idx}`;
const [visibleCols, setVisibleCols] = useState(() => {
  const saved = localStorage.getItem(scopeKey("sp500"));
  return new Set((JSON.parse(saved || "null") || DEFAULT_VISIBLE).slice(0, MAX_VISIBLE_COLUMNS));
});

// when index tab changes, load/swap per-scope settings
useEffect(() => {
  const saved = localStorage.getItem(scopeKey(activeIndex));
  setVisibleCols(new Set((JSON.parse(saved || "null") || DEFAULT_VISIBLE).slice(0, MAX_VISIBLE_COLUMNS)));
}, [activeIndex]);

useEffect(() => {
  localStorage.setItem(scopeKey(activeIndex), JSON.stringify([...visibleCols]));
}, [visibleCols, activeIndex]);



const activeFilterCount = useMemo(() =>
  Object.values(filters).filter((range) => range?.min !== "" || range?.max !== "").length,
  [filters]
);

const activeFilters = useMemo(() =>
  Object.entries(filters)
    .filter(([, range]) => range?.min !== "" || range?.max !== "")
    .map(([key, range]) => {
      const label = FILTER_LABELS[key] || key;
      const unit = FILTER_META[key]?.unit || "";
      const minSet = range?.min !== "";
      const maxSet = range?.max !== "";
      const description = minSet && maxSet
        ? `${label} ${range.min}${unit}-${range.max}${unit}`
        : minSet
          ? `${label} min ${range.min}${unit}`
          : `${label} max ${range.max}${unit}`;
      return { key, label, range, description };
    }),
  [filters]
);

const exportFilename = useMemo(() => {
  const date = new Date().toISOString().slice(0, 10);
  const filterParts = activeFilters.length > 3
    ? [`${activeFilters.length}-filters`]
    : activeFilters.map((filter) => filter.description);
  const parts = [
    "StockSleuth",
    UNIVERSE_LABELS[activeIndex] || activeIndex,
    ...filterParts,
    date,
  ].map(sanitizeFilenamePart).filter(Boolean);
  return `${parts.join("_")}.csv`;
}, [activeFilters, activeIndex]);

useEffect(() => {
  if (!filterVisible && !settingsVisible) return;
  const onKeyDown = (event) => {
    if (event.key === "Escape") {
      setFilterVisible(false);
      setSettingsVisible(false);
    }
  };
  window.addEventListener("keydown", onKeyDown);
  return () => window.removeEventListener("keydown", onKeyDown);
}, [filterVisible, settingsVisible]);

// Lock every outer scroll surface while Settings is open.
// Only the settings list itself is allowed to scroll.
useEffect(() => {
  if (!settingsVisible) return;

  const scrollTop = window.scrollY || document.documentElement.scrollTop || 0;
  const scrollLeft = window.scrollX || document.documentElement.scrollLeft || 0;

  const selectors = [
    "html",
    "body",
    "#root",
    ".App",
    ".whole",
    ".class-content",
    ".OtherContent",
  ];

  const elements = selectors
    .map((selector) => document.querySelector(selector))
    .filter(Boolean);

  const previous = elements.map((element) => ({
    element,
    overflow: element.style.overflow,
    overflowX: element.style.overflowX,
    overflowY: element.style.overflowY,
    position: element.style.position,
    top: element.style.top,
    left: element.style.left,
    width: element.style.width,
    height: element.style.height,
  }));

  elements.forEach((element) => {
    element.style.overflow = "hidden";
    element.style.overflowX = "hidden";
    element.style.overflowY = "hidden";
  });

  // Body position locking catches browsers/layouts where overflow:hidden alone
  // does not stop the page from moving.
  document.body.style.position = "fixed";
  document.body.style.top = `-${scrollTop}px`;
  document.body.style.left = `-${scrollLeft}px`;
  document.body.style.width = "100%";
  document.body.style.height = "100%";

  const settingsScroller = () =>
    document.getElementById("scanner-settings-scroll");

  const preventOuterScroll = (event) => {
    const scroller = settingsScroller();
    if (scroller && scroller.contains(event.target)) return;
    event.preventDefault();
  };

  const preventScrollKeys = (event) => {
    const scrollKeys = [
      "ArrowUp",
      "ArrowDown",
      "PageUp",
      "PageDown",
      "Home",
      "End",
      " ",
    ];
    if (!scrollKeys.includes(event.key)) return;

    const scroller = settingsScroller();
    if (scroller && scroller.contains(document.activeElement)) return;
    event.preventDefault();
  };

  window.addEventListener("wheel", preventOuterScroll, { passive: false });
  window.addEventListener("touchmove", preventOuterScroll, { passive: false });
  window.addEventListener("keydown", preventScrollKeys, { passive: false });

  return () => {
    window.removeEventListener("wheel", preventOuterScroll);
    window.removeEventListener("touchmove", preventOuterScroll);
    window.removeEventListener("keydown", preventScrollKeys);

    previous.forEach(
      ({
        element,
        overflow,
        overflowX,
        overflowY,
        position,
        top,
        left,
        width,
        height,
      }) => {
        element.style.overflow = overflow;
        element.style.overflowX = overflowX;
        element.style.overflowY = overflowY;
        element.style.position = position;
        element.style.top = top;
        element.style.left = left;
        element.style.width = width;
        element.style.height = height;
      }
    );

    window.scrollTo(scrollLeft, scrollTop);
  };
}, [settingsVisible]);

const clearAllFilters = () => {
  setFilters(buildEmptyFilters());
};

const removeFilter = (key) => {
  setFilters((prev) => ({
    ...prev,
    [key]: { min: "", max: "" },
  }));
};

const setVisibleColumn = (columnId, checked) => {
  setVisibleCols((prev) => {
    const next = new Set(prev);
    if (checked) {
      if (next.size >= MAX_VISIBLE_COLUMNS) return prev;
      next.add(columnId);
    } else if (next.size > 1) {
      next.delete(columnId);
    }
    return next;
  });
};

const setFilter = (key, bound, val) => {
  setFilters((prev) => ({
    ...prev,
    [key]: { ...(prev[key] || { min: "", max: "" }), [bound]: val },
  }));
};
const getFilterVal = (key, bound) => filters[key]?.[bound] ?? "";


const filterData = () => {
  const q = (searchQuery || "").toLowerCase();

  return BackupStockData.filter((item) => {
    // text match first
    const textMatch =
      (item.security_name || "").toLowerCase().includes(q) ||
      (item.symbol || "").toLowerCase().includes(q);
    if (!textMatch) return false;

    // numeric filters (all fields)
    for (const [key, range] of Object.entries(filters)) {
      const val = item[key];
      const scale = FILTER_META[key]?.inputScale || 1;
      const min = range?.min === "" ? "" : Number(range.min) / scale;
      const max = range?.max === "" ? "" : Number(range.max) / scale;
      if (!matchesNumRange(val, min, max)) return false;
    }
    return true;
  });
};


useEffect(() => {
  setStockData(filterData());
}, [filters, searchQuery, BackupStockData]);

  const handleSearch = debounce((value) => {
    setSearchQuery(value);
  }, 300);

  const clearFilter = () => {
    setSearchQuery("");
    setStockData(BackupStockData);
  };


  const clearfilter = () => {
    //console.log(BackupStockData);
    setStockData(BackupStockData);
  };
useEffect(() => {
  const fetchData = async () => {
    try {
      const url = `https://stocksleuth.xyz:5000/auth/getstocktable?index=${INDEX_PARAM[activeIndex] || "all"}`;
      const response = await fetch(url);
      const responseData = await response.json();

      // ---- OPTIONAL FALLBACK (if backend doesn't filter by index) ----
      let scoped = responseData;

      if (activeIndex === "watchlist") {
        if (!user?.displayname) {
          scoped = [];
        } else {
          const favoritesResponse = await getFavorites();
          const { favorites = [], watchlists = {} } = favoritesResponse.data || {};
          const watchlistSymbols = new Set([
            ...favorites,
            ...Object.values(watchlists).flatMap((watchlist) =>
              Array.isArray(watchlist?.components) ? watchlist.components : []
            ),
          ].filter(Boolean));
          scoped = responseData.filter((item) => watchlistSymbols.has(item.symbol));
        }
      }

      // ---- Clean / map fields exactly as before ----
      const cleanedData = scoped.map((item) => ({
        ...item,
        original_security_name: item.security_name || "",
        security_name: shortenSecurityName(item.security_name),

        price: numOrNull(item.price),
        beta: numOrNull(item.beta),
        peTTM: valuationOrNull(item.peTTM),
        psTTM: valuationOrNull(item.psTTM),
        currentDividendYieldTTM: numOrNull(item.currentDividendYieldTTM),
        pbQuarterly: valuationOrNull(item.pbQuarterly),
        epsTTM: numOrNull(item.epsTTM),
        mcap: numOrNull(item.marketCap), // still in millions

        // Totals
        revenueTTM: numOrNull(item.revenueTTM),
        netIncomeTTM: numOrNull(item.netIncomeTTM),
        ebitdaTTM: numOrNull(item.ebitdaTTM),
        freeCashFlowTTM: numOrNull(item.freeCashFlowTTM),

        // Margins
        grossMarginTTM: numOrNull(item.grossMarginTTM),
        operatingMarginTTM: numOrNull(item.operatingMarginTTM),
        profitMarginTTM: numOrNull(item.profitMarginTTM),
        fcfMarginTTM: numOrNull(item.fcfMarginTTM),

        // Returns
        roaTTM: numOrNull(item.roaTTM),
        roeTTM: numOrNull(item.roeTTM),
        roicTTM: numOrNull(item.roicTTM),

        // Leverage
        debtToAssets: numOrNull(item.debtToAssets),
        debtToEquity: numOrNull(item.debtToEquity),
        netDebtToEBITDA: numOrNull(item.netDebtToEBITDA),
        interestCoverageTTM: numOrNull(item.interestCoverageTTM),

        // Liquidity
        currentRatioQuarterly: numOrNull(item.currentRatioQuarterly),
        quickRatioQuarterly: numOrNull(item.quickRatioQuarterly),
        cashRatioQuarterly: numOrNull(item.cashRatioQuarterly),

        // Quality
        altmanZ: numOrNull(item.altmanZ),
        piotroskiF: numOrNull(item.piotroskiF),

        // Valuation
        enterpriseValue: numOrNull(item.enterpriseValue),
        evToEbitdaTTM: valuationOrNull(item.evToEbitdaTTM),
        evToSalesTTM: valuationOrNull(item.evToSalesTTM),
        priceToBookTangible: valuationOrNull(item.priceToBookTangible),

        // Growth
        revenueGrowthYoYTTM: numOrNull(item.revenueGrowthYoYTTM),
        netIncomeGrowthYoYTTM: numOrNull(item.netIncomeGrowthYoYTTM),
        epsGrowthYoYTTM: numOrNull(item.epsGrowthYoYTTM),
        fcfGrowthYoYTTM: numOrNull(item.fcfGrowthYoYTTM),
        revenueCAGR3Y: numOrNull(item.revenueCAGR3Y),
        epsCAGR3Y: numOrNull(item.epsCAGR3Y),
        revenueGrowthYoYLastQ: numOrNull(item.revenueGrowthYoYLastQ),
        revenueGrowthQoQLastQ: numOrNull(item.revenueGrowthQoQLastQ),
        netIncomeGrowthYoYLastQ: numOrNull(item.netIncomeGrowthYoYLastQ),
        netIncomeGrowthQoQLastQ: numOrNull(item.netIncomeGrowthQoQLastQ),
         DCFmean: numOrNull(item.DCFmean),
  DCFmedian: numOrNull(item.DCFmedian),
  DCFvalue: numOrNull(item.DCFvalue),
  dcfMedianVsPricePct: numOrNull(item.dcfMedianVsPricePct),
      }));

      const sortedData = cleanedData.sort((a, b) => (b.mcap ?? 0) - (a.mcap ?? 0));
      setStockData(sortedData);
      setBackupStockData(sortedData);
      setFilters(buildEmptyFilters());
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  fetchData();
}, [activeIndex, user?.displayname]); // <= refetch on universe/user change


const headers = [
  // existing
  { label: "Security Name", key: "security_name" },
  { label: "Beta", key: "beta" },
  { label: "P/E", key: "peTTM" },
  { label: "P/S", key: "psTTM" },
  { label: "Dividend Yield", key: "currentDividendYieldTTM" },
  { label: "P/B", key: "pbQuarterly" },
  { label: "EPS", key: "epsTTM" },
  { label: "Market Cap (Millions)", key: "mcap" },

  // NEW — core totals
  { label: "Revenue TTM", key: "revenueTTM" },
  { label: "Net Income TTM", key: "netIncomeTTM" },
  { label: "EBITDA TTM", key: "ebitdaTTM" },
  { label: "FCF TTM", key: "freeCashFlowTTM" },

  // NEW — margins
  { label: "Gross Margin TTM", key: "grossMarginTTM" },
  { label: "Operating Margin TTM", key: "operatingMarginTTM" },
  { label: "Profit Margin TTM", key: "profitMarginTTM" },
  { label: "FCF Margin TTM", key: "fcfMarginTTM" },

  // NEW — returns
  { label: "ROA TTM", key: "roaTTM" },
  { label: "ROE TTM", key: "roeTTM" },
  { label: "ROIC TTM", key: "roicTTM" },

  // NEW — leverage & coverage
  { label: "Debt/Assets", key: "debtToAssets" },
  { label: "Debt/Equity", key: "debtToEquity" },
  { label: "Net Debt/EBITDA", key: "netDebtToEBITDA" },
  { label: "Interest Coverage TTM", key: "interestCoverageTTM" },

  // NEW — liquidity
  { label: "Quick Ratio (Q)", key: "quickRatioQuarterly" },
  { label: "Cash Ratio (Q)", key: "cashRatioQuarterly" },

  // NEW — quality / risk
  { label: "Altman Z", key: "altmanZ" },
  { label: "Piotroski F", key: "piotroskiF" },

  // NEW — valuation
  { label: "Enterprise Value", key: "enterpriseValue" },
  { label: "EV/EBITDA TTM", key: "evToEbitdaTTM" },
  { label: "EV/Sales TTM", key: "evToSalesTTM" },
  { label: "P/Tangible Book", key: "priceToBookTangible" },

  // NEW — DCF
{ label: "DCF Mean", key: "DCFmean" },
{ label: "DCF Median", key: "DCFmedian" },
{ label: "DCF Value", key: "DCFvalue" },
{ label: "DCF Median vs Price (%)", key: "dcfMedianVsPricePct" },

  // NEW — growth
  { label: "Revenue YoY (TTM)", key: "revenueGrowthYoYTTM" },
  { label: "Net Income YoY (TTM)", key: "netIncomeGrowthYoYTTM" },
  { label: "EPS YoY (TTM)", key: "epsGrowthYoYTTM" },
  { label: "FCF YoY (TTM)", key: "fcfGrowthYoYTTM" },
  { label: "Revenue CAGR (3Y)", key: "revenueCAGR3Y" },
  { label: "EPS CAGR (3Y)", key: "epsCAGR3Y" },
  { label: "Revenue YoY (LastQ)", key: "revenueGrowthYoYLastQ" },
  { label: "Revenue QoQ (LastQ)", key: "revenueGrowthQoQLastQ" },
  { label: "NI YoY (LastQ)", key: "netIncomeGrowthYoYLastQ" },
  { label: "NI QoQ (LastQ)", key: "netIncomeGrowthQoQLastQ" },
];


const columns = useMemo(() => {
  // keep the original order you like:
  const order = [
    "security_name","symbol","price","mcap","beta",
    "peTTM","psTTM","currentDividendYieldTTM","pbQuarterly","epsTTM",
    // everything else follows in the registry order
    ...ALL_COLUMN_DEFS.map(c => c.id),
  ];
  const seen = new Set();

  const orderedUnique = order.filter(id => {
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  });

  return orderedUnique
    .map(id => ALL_COLUMN_DEFS.find(c => c.id === id))
    .filter(Boolean)
    .filter(def => visibleCols.has(def.id))
    .map(def => ({
      id: def.id,
      Header: def.Header,
      accessor: def.accessor,
      Cell: def.Cell,
      sortType: "basic",
    }));
}, [visibleCols]);


  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    state: { pageIndex, pageSize },
    previousPage,
    gotoPage,
    nextPage,
    setPageSize,
    canPreviousPage,
    filter,
    canNextPage,
    rows,
    size,
  } = useTable(
    {
      columns,
      data: stockData,
      initialState: {
        pageIndex: 0,
        pageSize: desktopPageSize,
      },
    },
    useSortBy,
    usePagination
  );

  useEffect(() => {
    let animationFrame;
    const updatePageSize = () => {
      cancelAnimationFrame(animationFrame);
      animationFrame = requestAnimationFrame(() => {
        setDesktopPageSize(measureDesktopPageSize());
      });
    };
    window.addEventListener("resize", updatePageSize);
    return () => {
      cancelAnimationFrame(animationFrame);
      window.removeEventListener("resize", updatePageSize);
    };
  }, []);

  useEffect(() => {
    const animationFrame = requestAnimationFrame(() => {
      setDesktopPageSize(measureDesktopPageSize());
    });
    return () => cancelAnimationFrame(animationFrame);
  }, [stockData.length, activeFilters.length]);

  useEffect(() => {
    if (pageSize === desktopPageSize) return;
    const firstVisibleRow = pageIndex * pageSize;
    setPageSize(desktopPageSize);
    gotoPage(Math.floor(firstVisibleRow / desktopPageSize));
  }, [desktopPageSize]);

const currentYear = useMemo(() => getEasternNowParts().year, []);
  const holidaysThisYear = useUsFederalHolidays(currentYear);
  const holidaysNextYear = useUsFederalHolidays(currentYear + 1);

  // Combined holidays (unique by name+observed date)
  const federalHolidays = useMemo(() => {
    const key = (h) => `${h.name}|${h.observed}`;
    const map = new Map();
    [...holidaysThisYear, ...holidaysNextYear].forEach((h) => map.set(key(h), h));
    return Array.from(map.values()).sort((a, b) => (a.observed < b.observed ? 1 : -1)); // newest first if you like
  }, [holidaysThisYear, holidaysNextYear]);

  useEffect(() => {
    const compute = () => {
      const parts = getEasternNowParts();
      const open = isUsTradingOpenET(parts);
      setMarketOpen(open);
    };
    compute();
    const id = setInterval(compute, 60 * 1000);
    return () => clearInterval(id);
  }, []);

  const startRange = pageIndex * pageSize + 1;
  const endRange = Math.min((pageIndex + 1) * pageSize, rows.length);
  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));

  useEffect(() => {
    setMobileVisibleCount(MOBILE_BATCH_SIZE);
  }, [rows.length]);

  useEffect(() => {
    const sentinel = mobileLoadMoreRef.current;
    if (!sentinel || typeof IntersectionObserver === "undefined") return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        setMobileVisibleCount((count) =>
          Math.min(count + MOBILE_BATCH_SIZE, rows.length)
        );
      },
      { rootMargin: "240px 0px" }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [rows.length, mobileVisibleCount]);






const renderFilterRow = (label, key) => {
  const unit = FILTER_META[key]?.unit;
  const renderBound = (bound, placeholder) => (
    <label className="scannerRangeBound">
      <input
        type="number"
        step="any"
        aria-label={`${label} ${bound === "min" ? "minimum" : "maximum"}`}
        placeholder={placeholder}
        value={getFilterVal(key, bound)}
        onChange={(e) => setFilter(key, bound, e.target.value === "" ? "" : Number(e.target.value))}
      />
      {unit && <span>{unit}</span>}
    </label>
  );

  return (
    <div className="scannerFilterRangeRow" key={key}>
      <div className="scannerFilterRangeLabel">{label}{unit && <span>{unit}</span>}</div>
      <div className="scannerRangeControl">
        {renderBound("min", "Minimum")}
        <span className="scannerRangeDivider" aria-hidden="true">—</span>
        {renderBound("max", "Maximum")}
      </div>
    </div>
  );
};








  return (
    <>
      <style>{`
        .pageSelector .frame {
          cursor: pointer !important;
          user-select: none;
          -webkit-user-select: none;
        }
        .pageSelector .text-wrapper,
        .pageSelector span {
          user-select: none;
          -webkit-user-select: none;
        }
        .scannerFilterRangeRow {
          display: grid;
          grid-template-columns: minmax(125px, 1fr) minmax(190px, 1.8fr);
          align-items: center;
          gap: 12px;
          margin-bottom: 7px;
        }
        .scannerFilterRangeLabel {
          min-width: 0;
          color: #5f6472;
          font-size: 11px;
          text-align: left;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .scannerFilterRangeLabel > span {
          margin-left: 5px;
          color: #7957ee;
          font-size: 9px;
          font-weight: 700;
        }
        .scannerRangeControl {
          display: grid;
          grid-template-columns: minmax(0, 1fr) 24px minmax(0, 1fr);
          align-items: center;
          min-width: 0;
          overflow: hidden;
          border: 1px solid #e1deeb;
          border-radius: 9px;
          background: #fff;
          transition: border-color .15s, box-shadow .15s;
        }
        .scannerRangeControl:focus-within {
          border-color: #7654ed;
          box-shadow: 0 0 0 3px rgba(118, 84, 237, .1);
        }
        .scannerRangeBound { display: flex; align-items: center; min-width: 0; }
        .scannerRangeBound input {
          width: 100%; min-width: 0; box-sizing: border-box; border: 0; background: transparent;
          padding: 7px 4px 7px 9px; color: #303342; font-size: 11px; outline: none;
        }
        .scannerRangeBound span { padding-right: 8px; color: #7957ee; font-size: 10px; font-weight: 700; }
        .scannerRangeDivider { color: #b2adbd; font-size: 11px; text-align: center; }
        @media (max-width: 700px) {
          .scannerFilterRangeRow { grid-template-columns: 1fr; gap: 5px; margin-bottom: 11px; }
        }
      `}</style>
      <div className="scannerPageMobileFix" style={{ width: "100%", height: "100vh" }}>
      <div>
        <div className="IndexesParent">
          <div
            className={`indexSelector${
              activeIndex === "sp500" ? "-Active" : ""
            }`}
            onClick={() => setActiveIndex("sp500")}
          >
            S&P 500
          </div>
          <div
            className={`indexSelector${
              activeIndex === "rus2000" ? "-Active" : ""
            }`}
            onClick={() => setActiveIndex("rus2000")}
          >
            RUSSELL 2000
          </div>
          <div
            className={`indexSelector${
              activeIndex === "nq100" ? "-Active" : ""
            }`}
            onClick={() => setActiveIndex("nq100")}
          >
            NASDAQ 100
          </div>
          <div
            className={`indexSelector${
              activeIndex === "watchlist" ? "-Active" : ""
            }`}
            onClick={() => setActiveIndex("watchlist")}
          >
            My Watchlist
          </div>
          {user && user.premium ? (
            <div
              className={`indexSelector${activeIndex === "all" ? "-Active" : ""}`}
              onClick={() => setActiveIndex("all")}
            >
              All Stocks
            
          </div>) : (<div
              className={`indexSelector${activeIndex === "all" ? "-Active" : ""}`}
            >
              All Stocks          <img src={lock} alt="Premium Lock" style={{ marginLeft: 5, height: 20, width: 20 }} /></div>)}
        </div>
        <div className="bigbox2" style={{ top: 148 }}>
          <div
            className="overlap-9"
            ref={scannerPanelRef}
            style={{ top: 0, borderRadius: "10px 30px 30px 30px" }}
          >
            <div style={{ height: "100%" }}>
              <h3
                style={{
                  margin: 0,
                  paddingLeft: 38,
                  paddingTop: 30,
                  textAlign: "left",
                }}
              >
                Stocks
              </h3>
              {marketOpen ? (
                <>
                  <h5
                    style={{
                      margin: 0,
                      paddingLeft: 38,
                      paddingTop: 7,
                      textAlign: "left",
                      color: "#16C098",
                    }}
                  >
                    Market Open
                  </h5>
                </>
              ) : (
                <>
                  <h5
                    style={{
                      color: "#fcc84e",
                      margin: 0,
                      paddingLeft: 38,
                      paddingTop: 7,
                      textAlign: "left",
                    }}
                  >
                    Market Closed
                  </h5>
                </>
              )}
              <div className="topBarScanner" style={{ gap: 16, display: "flex", alignItems: "center", justifyContent: "flex-end", position: 'absolute', top: '44px', right: '44px' }}>
                <div
                  className="Otherbar"
                  style={{ }}
                >
                  <div className="search">
                  <div className="overlap-group">
                    <input
                      placeholderTextColor={"red"}
                      className="div"
                      placeholder="Search"
                      type="text"
                      onChange={(e) => handleSearch(e.target.value)}
                    />
                    <img className="img" alt="Search" src={search1} />
                  </div>
                </div>
              </div>
              <div
                className="sortby"
                style={{}}
                onClick={() => {setFilterVisible(!filterVisible);setSettingsVisible(false)}}
              >
                <div className="search" style={{cursor: "pointer"}}>
                  <div className="overlap-group">
                    <p
                      style={{ margin: 0, color: "#b5b7c0", left: "15px", userSelect: "none" }}
                      className="dropdown"
                    >
                      Filter{activeFilterCount ? ` · ${activeFilterCount}` : ""}
                    </p>
                  </div>
                </div>
              </div>
              <div
                className="sortby"
                style={{}}
                onClick={() => {setSettingsVisible(!settingsVisible);setFilterVisible(false)}}
              >
                <div className="search" style={{cursor: "pointer"}}>
                  <div className="overlap-group">
                    <p
                      style={{ margin: 0, color: "#b5b7c0", left: "15px", userSelect: "none" }}
                      className="dropdown"
                    >
                      Columns
                    </p>
                  </div>
                </div>
              </div>
              <div
                className="download"
                style={{}}
              >
                <div className="search">
                  <div className="overlap-group">
                    <CSVLink
                      data={stockData}
                      headers={headers}
                      filename={exportFilename}
                      className="btn btn-primary"
                      target="_blank"
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          width: "100%",
                          height: "100%",
                        }}
                      >
                        <svg
                          fill="rgb(181, 183, 192)" // Change to white or another contrasting color
                          height="20px" // Define the size of the SVG icon
                          width="20px" // Define the size of the SVG icon
                          version="1.1"
                          id="Layer_1"
                          viewBox="0 0 512 512" // This is the only required attribute
                        >
                          <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                          <g
                            id="SVGRepo_tracerCarrier"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          ></g>
                          <g id="SVGRepo_iconCarrier">
                            <g>
                              <path d="M440.125,0H0v512h512V71.875L440.125,0z M281.6,31.347h31.347v94.041H281.6V31.347z M136.359,31.347h113.894v125.388 h94.041V31.347h32.392v156.735H136.359V31.347z M417.959,480.653H94.041V344.816h323.918V480.653z M417.959,313.469H94.041 v-31.347h323.918V313.469z M480.653,480.653h-31.347V250.775H62.694v229.878H31.347V31.347h73.665v188.082h303.02V31.347h19.108 l53.512,53.512V480.653z"></path>
                            </g>
                          </g>
                        </svg>
                      </div>
                    </CSVLink>
                  </div>
                </div>
              </div></div>
              {activeFilters.length > 0 && (
                <div className="scannerActiveFilterBar" aria-label="Active filters">
                  <div className="scannerActiveFilterChips">
                    {activeFilters.map((filter) => (
                      <button
                        key={filter.key}
                        type="button"
                        className="scannerActiveFilterChip"
                        onClick={() => removeFilter(filter.key)}
                        title={`Remove ${filter.label} filter`}
                      >
                        <span>{filter.description}</span>
                        <span aria-hidden="true">×</span>
                      </button>
                    ))}
                  </div>
                  <button
                    type="button"
                    className="scannerClearFilters"
                    onClick={clearAllFilters}
                  >
                    Clear all
                  </button>
                </div>
              )}
              {filterVisible && (
                <div
                  className="box3"
                  onClick={() => setFilterVisible(false)}
                  style={{ width: "100%", zIndex: 5, position: "absolute", top: 100, bottom: 75, display: "flex", justifyContent: "center", alignItems: "center", background: "rgba(248,249,252,0.76)", backdropFilter: "blur(3px)", fontFamily: "Poppins, sans-serif" }}
                >
                  <div
                    onClick={(e) => e.stopPropagation()}
                    style={{ width: "94%", maxHeight: "92%", overflow: "hidden", background: "#fff", border: "1px solid #ececf3", borderRadius: 18, boxShadow: "0 14px 34px rgba(63,48,120,0.12)", display: "flex", flexDirection: "column" }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 18px 12px", borderBottom: "1px solid #f0f1f5" }}>
                      <div style={{ textAlign: "left" }}>
                        <div style={{ fontSize: 15, fontWeight: 700, color: "#242635" }}>Filter stocks</div>
                        <div style={{ fontSize: 11, color: "#9296a3", marginTop: 2 }}>Set any minimum or maximum values you need.</div>
                      </div>
                      <button onClick={() => setFilterVisible(false)} style={{ border: 0, background: "transparent", cursor: "pointer", fontSize: 20, color: "#858997", lineHeight: 1, fontFamily: "Poppins, sans-serif" }}>×</button>
                    </div>
                    <div style={{ overflowY: "auto", padding: 14, display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 12 }}>
                      {[
                        ["Valuation", FILTER_SECTIONS.valuation], ["Growth", FILTER_SECTIONS.growth], ["Quality", FILTER_SECTIONS.quality],
                        ["Liquidity", FILTER_SECTIONS.liquidity], ["Profitability", FILTER_SECTIONS.profitability], ["Leverage & Risk", FILTER_SECTIONS.leverage], ["DCF", FILTER_SECTIONS.dcf],
                      ].map(([title, items]) => (
                        <section key={title} style={{ background: "#fafafe", border: "1px solid #efeff6", borderRadius: 14, padding: 12, alignSelf: "start" }}>
                          <div style={{ fontSize: 12, fontWeight: 700, color: "#343746", textAlign: "left", marginBottom: 10 }}>{title}</div>
                          {items.map((f) => renderFilterRow(f.label, f.key))}
                        </section>
                      ))}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "12px 16px", borderTop: "1px solid #f0f1f5", background: "#fff" }}>
                      <span style={{ color: "#9296a3", fontSize: 11 }}>{activeFilterCount} active filter{activeFilterCount === 1 ? "" : "s"}</span>
                      <div style={{ display: "flex", gap: 8 }}>
                        <button onClick={clearAllFilters} style={{ border: "1px solid #e2e3ea", background: "#fff", color: "#626675", borderRadius: 9, padding: "7px 12px", cursor: "pointer", fontSize: 11, fontWeight: 600, fontFamily: "Poppins, sans-serif" }}>Reset</button>
                        <button onClick={() => setFilterVisible(false)} style={{ border: 0, background: "#5932ea", color: "#fff", borderRadius: 9, padding: "7px 16px", cursor: "pointer", fontSize: 11, fontWeight: 600, boxShadow: "0 6px 14px rgba(89,50,234,0.22)", fontFamily: "Poppins, sans-serif" }}>Done</button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {settingsVisible && (
                <div
                  className="box3"
                  onClick={() => setSettingsVisible(false)}
                  style={{ width: "100%", zIndex: 5, position: "absolute", top: 100, bottom: 75, display: "flex", justifyContent: "center", alignItems: "center", background: "rgba(248,249,252,0.76)", backdropFilter: "blur(3px)", fontFamily: "Poppins, sans-serif" }}
                >
                  <div
                    onClick={(e) => e.stopPropagation()}
                    style={{ width: "92%", maxHeight: "90%", overflow: "hidden", background: "#fff", border: "1px solid #ececf3", borderRadius: 18, boxShadow: "0 14px 34px rgba(63,48,120,0.12)", display: "flex", flexDirection: "column" }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 14, padding: "16px 18px 12px", borderBottom: "1px solid #f0f1f5" }}>
                      <div style={{ textAlign: "left", minWidth: 180 }}>
                        <div style={{ fontSize: 15, fontWeight: 700, color: "#242635" }}>Table columns</div>
                        <div style={{ fontSize: 11, color: "#9296a3", marginTop: 2 }}>Choose up to {MAX_VISIBLE_COLUMNS} columns to display.</div>
                      </div>
                      <input value={settingsSearch} onChange={(e) => setSettingsSearch(e.target.value)} placeholder="Search columns" style={{ width: 210, border: "1px solid #e5e6ed", background: "#fafafe", borderRadius: 10, padding: "8px 11px", outline: "none", fontSize: 11, fontFamily: "Poppins, sans-serif" }} />
                      <button onClick={() => setSettingsVisible(false)} style={{ border: 0, background: "transparent", cursor: "pointer", fontSize: 20, color: "#858997", lineHeight: 1, fontFamily: "Poppins, sans-serif" }}>×</button>
                    </div>
                    <div id="scanner-settings-scroll" style={{ overflowY: "auto", overscrollBehavior: "contain", minHeight: 0, padding: 14, columnCount: 3, columnGap: 12, fontFamily: "Poppins, sans-serif" }}>
                      {COLUMN_GROUPS.map((group) => {
                        const cols = ALL_COLUMN_DEFS.filter((c) => c.group === group && `${c.Header} ${c.id}`.toLowerCase().includes(settingsSearch.trim().toLowerCase()));
                        if (!cols.length) return null;
                        return (
                          <section key={group} style={{ background: "#fafafe", border: "1px solid #efeff6", borderRadius: 14, padding: 12, marginBottom: 12, breakInside: "avoid", WebkitColumnBreakInside: "avoid", display: "inline-block", width: "100%", boxSizing: "border-box", fontFamily: "Poppins, sans-serif" }}>
                            <div style={{ fontSize: 12, fontWeight: 700, color: "#343746", textAlign: "left", marginBottom: 9 }}>{group}</div>
                            {cols.map((c) => {
                              const checked = visibleCols.has(c.id);
                              const disabled = !checked && visibleCols.size >= MAX_VISIBLE_COLUMNS;
                              return (
                                <label key={c.id} style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 8, fontSize: 11, cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.42 : 1, color: "#454957", userSelect: "none", fontFamily: "Poppins, sans-serif" }}>
                                  <span style={{ width: 16, height: 16, borderRadius: 5, border: checked ? "1px solid #5932ea" : "1px solid #cfd2dc", background: checked ? "#5932ea" : "#fff", display: "inline-flex", alignItems: "center", justifyContent: "center", flex: "0 0 auto", boxShadow: checked ? "0 3px 8px rgba(89,50,234,0.18)" : "none" }}>
                                    {checked && <span style={{ color: "#fff", fontSize: 11, lineHeight: 1 }}>✓</span>}
                                  </span>
                                  <input type="checkbox" checked={checked} disabled={disabled} onChange={(e) => setVisibleColumn(c.id, e.target.checked)} style={{ position: "absolute", opacity: 0, pointerEvents: "none" }} />
                                  <span>{c.Header}</span>
                                </label>
                              );
                            })}
                          </section>
                        );
                      })}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "12px 16px", borderTop: "1px solid #f0f1f5", background: "#fff" }}>
                      <div style={{ textAlign: "left" }}>
                        <div style={{ color: visibleCols.size >= MAX_VISIBLE_COLUMNS ? "#5932ea" : "#777b89", fontSize: 11, fontWeight: 700 }}>{visibleCols.size} of {MAX_VISIBLE_COLUMNS} columns selected</div>
                        {visibleCols.size >= MAX_VISIBLE_COLUMNS && <div style={{ color: "#999daa", fontSize: 10, marginTop: 2 }}>Remove one column before adding another.</div>}
                      </div>
                      <div style={{ display: "flex", gap: 8 }}>
                        <button onClick={() => setVisibleCols(new Set(DEFAULT_VISIBLE.slice(0, MAX_VISIBLE_COLUMNS)))} style={{ border: "1px solid #e2e3ea", background: "#fff", color: "#626675", borderRadius: 9, padding: "7px 12px", cursor: "pointer", fontSize: 11, fontWeight: 600, fontFamily: "Poppins, sans-serif" }}>Reset to default</button>
                        <button onClick={() => setSettingsVisible(false)} style={{ border: 0, background: "#5932ea", color: "#fff", borderRadius: 9, padding: "7px 16px", cursor: "pointer", fontSize: 11, fontWeight: 600, boxShadow: "0 6px 14px rgba(89,50,234,0.22)", fontFamily: "Poppins, sans-serif" }}>Done</button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <table
                ref={scannerTableRef}
                className="ScannerTable"
                {...getTableProps()}
                style={{
                  maxWidth: "96%",
                  width: "96%",
                  left: "2%",
                  right: "2%",
                  position: "absolute",
                  fontSize: 12,
                  top: 97,
                  height: "76.48%",
                }}
              >
                <thead style={{ height: "5.88%", width: "100%" }}>
                  {headerGroups.map((headerGroup) => (
                    <tr
                      {...headerGroup.getHeaderGroupProps()}
                      style={{
                        height: "5.88%",
                        color: "#8a8a8a",
                        fontWeight: "bolder",
                      }}
                    >
                      {headerGroup.headers.map((column, columnIndex) => (
                        <th
                          {...column.getHeaderProps(
                            column.getSortByToggleProps()
                          )}
                          style={{
                            width: columnIndex === 0 ? "20%" : "7%",
                            fontWeight: "500",
                          }}
                        >
                          {column.render("Header")}
                          <span>
                            {column.isSorted
                              ? column.isSortedDesc
                                ? " ↓"
                                : " ↑"
                              : ""}
                          </span>
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody {...getTableBodyProps()} style={{ height: "100%" }}>
                  {page.map((row) => {
                    prepareRow(row);
                    return (
                      <tr
                        {...row.getRowProps()}
                        style={{ height: "5.88%", width: "100%" }}
                      >
                        {row.cells.map((cell, columnIndex) => (
                          <td
                            {...cell.getCellProps()}
                            style={{
                              maxHeight: "20px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              width: columnIndex === 0 ? "20%" : "7%",
                            }}
                          >
                            {cell.render("Cell")}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              <div className="mobileScannerCards">
                {rows.slice(0, mobileVisibleCount).map((row) => {
                  prepareRow(row);
                  const [companyCell, ...metricCells] = row.cells;

                  return (
                    <article {...row.getRowProps()} className="mobileScannerCard">
                      <div className="mobileScannerCardCompany">
                        {companyCell?.render("Cell")}
                      </div>
                      <div className="mobileScannerMetricGrid">
                        {metricCells.map((cell) => (
                          <div {...cell.getCellProps()} className="mobileScannerMetric">
                            <span>{cell.column.Header}</span>
                            <strong>{cell.render("Cell")}</strong>
                          </div>
                        ))}
                      </div>
                    </article>
                  );
                })}
              </div>
              {mobileVisibleCount < rows.length && (
                <div
                  ref={mobileLoadMoreRef}
                  className="mobileScannerLoadMore"
                  aria-hidden="true"
                >
                  <span />
                  <span />
                  <span />
                </div>
              )}

              <div
                className="scannerResultsSummary"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginTop: "10px",
                  bottom: "35px",
                  position: "absolute",
                  left: "38px",
                }}
              >
                <span style={{ color: "#b5b7c0", fontSize: 14 }}>
                  Showing {startRange} to {endRange} of {rows.length} entries
                </span>
              </div>

              {totalPages <= 5 ? (
                <div className="pageSelector">
                  <div
                    className="frame"
                    onClick={() => previousPage()}
                    disabled={!canPreviousPage}
                    style={{ cursor: canPreviousPage ? "pointer" : "default" }}
                  >
                    <div className="text-wrapper" style={{ pointerEvents: "none" }}>&lt;</div>
                  </div>

                  {Array.from({ length: totalPages }, (_, index) => (
                    <div
                      key={index}
                      className="frame"
                      onClick={() => gotoPage(index)}
                      style={
                        index === pageIndex
                          ? {
                              borderColor: "#5932ea",
                              backgroundColor: "#5932ea",
                              color: "#ffffff",
                            }
                          : undefined
                      }
                    >
                      <div
                        className="text-wrapper"
                        style={{
                          color: index === pageIndex ? "#ffffff" : undefined,
                          pointerEvents: "none",
                        }}
                      >
                        {index + 1}
                      </div>
                    </div>
                  ))}

                  <div
                    className="frame"
                    onClick={() => nextPage()}
                    disabled={!canNextPage}
                    style={{ cursor: canNextPage ? "pointer" : "default" }}
                  >
                    <div className="text-wrapper" style={{ pointerEvents: "none" }}>&gt;</div>
                  </div>
                </div>
              ) : pageIndex == 0 ? (
                <div className="pageSelector">
                  <div
                    className="frame"
                    onClick={() => previousPage()}
                    disabled={!canPreviousPage}
                    style={{ cursor: "default" }}
                  >
                    <div className="text-wrapper" style={{ pointerEvents: "none" }}>&lt;</div>
                  </div>

                  <div
                    className="frame"
                    onClick={() => gotoPage(pageIndex)}
                    style={{
                      borderColor: "#5932ea",
                      backgroundColor: "#5932ea",
                      color: "#ffffff",
                    }}
                  >
                    <div className="text-wrapper" style={{ color: "#ffffff", pointerEvents: "none" }}>
                      {pageIndex + 1}
                    </div>
                  </div>
                  <div
                    className="frame"
                    onClick={() => gotoPage(pageIndex + 1)}
                  >
                    <div className="text-wrapper" style={{ pointerEvents: "none" }}>{pageIndex + 2}</div>
                  </div>
                  <div
                    className="frame"
                    onClick={() => gotoPage(pageIndex + 2)}
                  >
                    <div className="text-wrapper" style={{ pointerEvents: "none" }}>{pageIndex + 3}</div>
                  </div>
                  <div
                    className="frame"
                    onClick={() => gotoPage(pageIndex + 3)}
                  >
                    <div className="text-wrapper" style={{ pointerEvents: "none" }}>{pageIndex + 4}</div>
                  </div>
                  <span style={{ marginLeft: "12px" }}>...</span>
                  <div
                    className="frame"
                    onClick={() =>
                      gotoPage(Math.ceil(stockData.length / pageSize) - 1)
                    }
                  >
                    <div className="text-wrapper" style={{ pointerEvents: "none" }}>
                      {Math.ceil(stockData.length / pageSize)}
                    </div>
                  </div>

                  <div
                    className="frame"
                    onClick={() => nextPage()}
                    disabled={!canNextPage}
                  >
                    <div className="text-wrapper" style={{ pointerEvents: "none" }}>&gt;</div>
                  </div>
                </div>
              ) : pageIndex == 1 ? (
                <div className="pageSelector">
                  <div
                    className="frame"
                    onClick={() => previousPage()}
                    disabled={!canPreviousPage}
                  >
                    <div className="text-wrapper">&lt;</div>
                  </div>

                  <div
                    className="frame"
                    onClick={() => gotoPage(pageIndex - 1)}
                  >
                    <div className="text-wrapper">{pageIndex}</div>
                  </div>
                  <div
                    className="frame"
                    onClick={() => gotoPage(pageIndex + 1)}
                    style={{
                      borderColor: "#5932ea",
                      backgroundColor: "#5932ea",
                      color: "#ffffff",
                    }}
                  >
                    <div className="text-wrapper" style={{ color: "#ffffff" }}>
                      {pageIndex + 1}
                    </div>
                  </div>
                  <div
                    className="frame"
                    onClick={() => gotoPage(pageIndex + 3)}
                  >
                    <div className="text-wrapper">{pageIndex + 2}</div>
                  </div>
                  <div
                    className="frame"
                    onClick={() => gotoPage(pageIndex + 2)}
                  >
                    <div className="text-wrapper">{pageIndex + 3}</div>
                  </div>
                  <span style={{ marginLeft: "12px" }}>...</span>
                  <div
                    className="frame"
                    onClick={() =>
                      gotoPage(Math.ceil(stockData.length / pageSize) - 1)
                    }
                  >
                    <div className="text-wrapper">
                      {Math.ceil(stockData.length / pageSize)}
                    </div>
                  </div>

                  <div
                    className="frame"
                    onClick={() => nextPage()}
                    disabled={!canNextPage}
                  >
                    <div className="text-wrapper">&gt;</div>
                  </div>
                </div>
              ) : pageIndex == Math.ceil(stockData.length / pageSize) - 1 ? (
                <div className="pageSelector">
                  <div
                    className="frame"
                    onClick={() => previousPage()}
                    disabled={!canPreviousPage}
                  >
                    <div className="text-wrapper">&lt;</div>
                  </div>

                  <div className="frame" onClick={() => gotoPage(0)}>
                    <div className="text-wrapper">1</div>
                  </div>
                  <span style={{ marginLeft: "12px" }}>...</span>
                  <div
                    className="frame"
                    onClick={() => gotoPage(pageIndex - 3)}
                  >
                    <div className="text-wrapper">{pageIndex - 2}</div>
                  </div>
                  <div
                    className="frame"
                    onClick={() => gotoPage(pageIndex - 2)}
                  >
                    <div className="text-wrapper">{pageIndex - 1}</div>
                  </div>
                  <div
                    className="frame"
                    onClick={() => gotoPage(pageIndex - 1)}
                  >
                    <div className="text-wrapper">{pageIndex}</div>
                  </div>

                  <div
                    style={{
                      borderColor: "#5932ea",
                      backgroundColor: "#5932ea",
                      color: "#ffffff",
                    }}
                    className="frame"
                    onClick={() =>
                      gotoPage(Math.ceil(stockData.length / pageSize) - 1)
                    }
                  >
                    <div className="text-wrapper" style={{ color: "#ffffff" }}>
                      {Math.ceil(stockData.length / pageSize)}
                    </div>
                  </div>

                  <div
                    className="frame"
                    onClick={() => nextPage()}
                    disabled={!canNextPage}
                  >
                    <div className="text-wrapper">&gt;</div>
                  </div>
                </div>
              ) : pageIndex == Math.ceil(stockData.length / pageSize) - 2 ? (
                <div className="pageSelector">
                  <div
                    className="frame"
                    onClick={() => previousPage()}
                    disabled={!canPreviousPage}
                  >
                    <div className="text-wrapper">&lt;</div>
                  </div>

                  <div className="frame" onClick={() => gotoPage(0)}>
                    <div className="text-wrapper">1</div>
                  </div>
                  <span style={{ marginLeft: "12px" }}>...</span>
                  <div
                    className="frame"
                    onClick={() => gotoPage(pageIndex - 2)}
                  >
                    <div className="text-wrapper">{pageIndex - 1}</div>
                  </div>
                  <div
                    className="frame"
                    onClick={() => gotoPage(pageIndex - 1)}
                  >
                    <div className="text-wrapper">{pageIndex}</div>
                  </div>
                  <div
                    style={{
                      borderColor: "#5932ea",
                      backgroundColor: "#5932ea",
                      color: "#ffffff",
                    }}
                    className="frame"
                    onClick={() => gotoPage(pageIndex)}
                  >
                    <div className="text-wrapper" style={{ color: "#ffffff" }}>
                      {pageIndex + 1}
                    </div>
                  </div>

                  <div
                    className="frame"
                    onClick={() =>
                      gotoPage(Math.ceil(stockData.length / pageSize) - 1)
                    }
                  >
                    <div className="text-wrapper">
                      {Math.ceil(stockData.length / pageSize)}
                    </div>
                  </div>

                  <div
                    className="frame"
                    onClick={() => nextPage()}
                    disabled={!canNextPage}
                  >
                    <div className="text-wrapper">&gt;</div>
                  </div>
                </div>
              ) : (
                <div className="pageSelector">
                  <div
                    className="frame"
                    onClick={() => previousPage()}
                    disabled={!canPreviousPage}
                  >
                    <div className="text-wrapper">&lt;</div>
                  </div>

                  <div className="frame" onClick={() => gotoPage(0)}>
                    <div className="text-wrapper">1</div>
                  </div>
                  <span style={{ marginLeft: "12px" }}>...</span>
                  <div
                    className="frame"
                    onClick={() => gotoPage(pageIndex - 1)}
                  >
                    <div className="text-wrapper">{pageIndex}</div>
                  </div>
                  <div
                    className="frame"
                    onClick={() => gotoPage(pageIndex)}
                    style={{
                      borderColor: "#5932ea",
                      backgroundColor: "#5932ea",
                      color: "#ffffff",
                    }}
                  >
                    <div className="text-wrapper" style={{ color: "#ffffff" }}>
                      {pageIndex + 1}
                    </div>
                  </div>
                  <div
                    className="frame"
                    onClick={() => gotoPage(pageIndex + 1)}
                  >
                    <div className="text-wrapper">{pageIndex + 2}</div>
                  </div>
                  <span style={{ marginLeft: "12px" }}>...</span>
                  <div
                    className="frame"
                    onClick={() =>
                      gotoPage(Math.ceil(stockData.length / pageSize) - 1)
                    }
                  >
                    <div className="text-wrapper">
                      {Math.ceil(stockData.length / pageSize)}
                    </div>
                  </div>

                  <div
                    className="frame"
                    onClick={() => nextPage()}
                    disabled={!canNextPage}
                  >
                    <div className="text-wrapper">&gt;</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      </div>
    </>
  );
};
export default Scanner;
